import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import ConfirmDialog from '../common/ConfirmDialog';
import './EmailConfigurations.css';
import Select2Dropdown from '../common/Select2Dropdown';
import { getAuthHeaders } from '../../auth';
import PermitConfiguration from '../masters/PermitConfiguration'; // Import the configuration component
import useSortableTable from '../common/useSortableTable';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_URL = '/api/admin/permits/';

const Permits = () => {
    const { hasPermission } = usePermissions(); // 2. Get the permission checker function
    const [permits, setPermits] = useState([]); // Displayed permits
    const [allPermits, setAllPermits] = useState([]); // All loaded permits for filtering
    const [loading, setLoading] = useState(true);
    const [permitsLoaded, setPermitsLoaded] = useState(false); // Track if permits are loaded in background
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPermit, setCurrentPermit] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: () => {}, onCancel: () => {} });
    const [uploading, setUploading] = useState(false);
    const [showPermitConfigModal, setShowPermitConfigModal] = useState(false); // State for the new modal

    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [searchBy, setSearchBy] = useState('hs_code'); // Default to HS Code
    const [selectedPermitType, setSelectedPermitType] = useState('');
    const [permitTypes, setPermitTypes] = useState([]);
    const [permitMappings, setPermitMappings] = useState({});
    const { items: sortedPermits, requestSort, getSortIndicator } = useSortableTable(permits);

    const filterPermitTypes = useMemo(() => permitTypes, [permitTypes]);

    // Define fetchAllPermits to load all permits in background
    const fetchAllPermits = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch permits');
            const data = await res.json();
            setAllPermits(data.results || data);
            setPermitsLoaded(true);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, []); // No dependencies - only fetches all permits once

    // This useEffect calls fetchAllPermits once on page load
    useEffect(() => {
        if (hasPermission('PERMITS', 'can_view')) {
            fetchAllPermits();
        }
    }, [fetchAllPermits, hasPermission]);

    // Debounce search text to avoid filtering on every keystroke
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 300); // 300ms delay

        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    // This useEffect applies client-side filtering when debounced search text or permit type changes
    useEffect(() => {
        if (permitsLoaded) {
            let filtered = allPermits;

            if (debouncedSearchText.length >= 3) {
                const searchLower = debouncedSearchText.toLowerCase();
                if (searchBy === 'hs_code') {
                    filtered = filtered.filter(p =>
                        (p.hs_code || '').toLowerCase().includes(searchLower)
                    );
                } else if (searchBy === 'description') {
                    filtered = filtered.filter(p =>
                        (p.description || '').toLowerCase().includes(searchLower)
                    );
                }
            }

            if (selectedPermitType && selectedPermitType !== 'ALL') {
                filtered = filtered.filter(p => p.permit === selectedPermitType);
            }

            setPermits(filtered);
        }
    }, [allPermits, debouncedSearchText, selectedPermitType, permitsLoaded, searchBy]);

    useEffect(() => {
        const fetchPermitTypesAndMappings = async () => {
            try {
                const [typesRes, mappingsRes] = await Promise.all([
                    fetch(`${API_URL}unique-permit-types/`, { headers: getAuthHeaders() }),
                    fetch('/api/admin/permit-document-mappings/', { headers: getAuthHeaders() })
                ]);
                
                if (typesRes.ok) {
                    const data = await typesRes.json();
                    setPermitTypes(data.map(pt => ({ id: pt, text: pt })));
                }
                
                if (mappingsRes.ok) {
                    const mappingsData = await mappingsRes.json();
                    const mappingsDict = {};
                    mappingsData.forEach(m => {
                        mappingsDict[m.permit_type] = m.background_color || '';
                    });
                    setPermitMappings(mappingsDict);
                }
            } catch (error) {
                toast.error(error.message);
            }
        };
        if (hasPermission('PERMITS', 'can_view')) {
            fetchPermitTypesAndMappings();
        }
    }, [hasPermission, showPermitConfigModal]); // Refresh mappings when config modal closes

    // Create a stable function for the onChange handler
    const handlePermitTypeChange = useCallback((option) => {
        console.log('Selected option:', option);
        setSelectedPermitType(option);
    }, []);

    const handleOpenModal = (permit = null) => {
        // 4. Permission check for opening the modal
        const canOpen = permit ? hasPermission('PERMITS', 'can_edit') : hasPermission('PERMITS', 'can_create');
        if (!canOpen) {
            toast.error("You don't have permission to perform this action.");
            return;
        }
        setCurrentPermit(permit ? { ...permit } : { hs_code: '', description: '', permit: '' });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentPermit(null);
    };
    
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setCurrentPermit(prev => ({ ...prev, [name]: value }));
    };

    const handleCreatePermitType = (term) => {
        const newOption = { id: term, text: term };
        setPermitTypes(prev => [...prev, newOption]);
        return newOption;
    };

    const handleSave = async () => {
        if (!currentPermit || !currentPermit.hs_code || !currentPermit.permit) {
            toast.warn('HS Code and Permit are required.');
            return;
        }

        const isEditing = !!currentPermit.id;
        // 5. Permission check before saving
        const canSave = isEditing ? hasPermission('PERMITS', 'can_edit') : hasPermission('PERMITS', 'can_create');
        if (!canSave) {
            toast.error("You don't have permission to save.");
            return;
        }

        const url = isEditing ? `${API_URL}${currentPermit.id}/` : API_URL;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: getAuthHeaders(), // isFormData = false
                body: JSON.stringify(currentPermit),
            });

            const result = await response.json();
            if (!response.ok) {
                const errorMsg = Object.values(result).flat().join(' ');
                throw new Error(errorMsg || 'Failed to save permit.');
            }

            toast.success(`Permit ${isEditing ? 'updated' : 'added'} successfully!`);
            handleCloseModal();
            fetchAllPermits(); // Reload all permits
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = (id) => {
        // 6. Permission check before showing confirmation
        if (!hasPermission('PERMITS', 'can_delete')) {
            toast.error("You don't have permission to delete permits.");
            return;
        }
        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: 'Are you sure you want to delete this permit?',
            onConfirm: async () => {
                try {
                    const res = await fetch(`${API_URL}${id}/`, { method: 'DELETE', headers: getAuthHeaders() });
                    if (!res.ok) throw new Error('Failed to delete permit');
                    toast.success('Permit deleted successfully!');
                    setConfirmDialog({ open: false, title: '', message: '', onConfirm: () => {}, onCancel: () => {} });
                    fetchAllPermits(); // Reload all permits
                } catch (error) {
                    toast.error(error.message);
                }
            },
            onCancel: () => setConfirmDialog({ open: false, title: '', message: '', onConfirm: () => {}, onCancel: () => {} }),
        });
    };

    const handleFileUpload = async (e) => {
        // 7. Permission check before uploading
        if (!hasPermission('PERMITS', 'can_create')) {
            toast.error("You don't have permission to upload permits.");
            e.target.value = null; // Reset file input
            return;
        }
        const files = e.target.files;
        if (files.length === 0) return;

        setUploading(true);
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        try {
            const res = await fetch(`${API_URL}upload-permits/`, {
                method: 'POST',
                headers: getAuthHeaders(true), // isFormData = true
                body: formData,
            });
            const result = await res.json();
            if (!res.ok) {
                 throw new Error(result.error || 'File upload failed');
            }
            toast.success(`${result.added} new permits added. ${result.skipped_duplicates} duplicates skipped.`);
            if (result.errors && result.errors.length > 0) {
                result.errors.forEach(err => toast.warning(err));
            }
            fetchAllPermits(); // Reload all permits
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUploading(false);
            e.target.value = null; // Reset file input
        }
    };

    // 8. Top-level permission check for viewing the page
    if (!hasPermission('PERMITS', 'can_view')) {
        return (
            <div>
                <h3>Permission Denied</h3>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div>
            {/* 9. Conditionally render the upload section */}
            {hasPermission('PERMITS', 'can_create') && (
                <div className="admin-utility-section">
                    <h3>Upload Permits from Excel</h3>
                    <p className="instructions">
                        Upload an Excel file (.xls, .xlsx) with 3 columns in order: <strong>HS Code</strong>, <strong>Description</strong>, <strong>Permit</strong>. The first row is the header and will be skipped.
                    </p>
                    <div className="upload-controls">
                        <input id="permit-excel-input" type="file" multiple accept=".xls,.xlsx" onChange={handleFileUpload} disabled={uploading} />
                    </div>
                    {uploading && <p>Uploading, please wait...</p>}
                </div>
            )}

            <div className="admin-utility-section" style={{ marginTop: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Manage Permits</h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* 10. Conditionally render the mapping and add buttons */}
                        {hasPermission('PERMIT_CONFIG', 'can_view') && (
                            <button onClick={() => setShowPermitConfigModal(true)} className="add-btn">Permit Mappings</button>
                        )}
                        {hasPermission('PERMITS', 'can_create') && (
                            <button onClick={() => handleOpenModal()} className="add-btn">Add New Permit</button>
                        )}
                    </div>
                </div>
                {/* Filter Controls */}
                <div className="filter-controls" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                        <Select2Dropdown
                            options={[
                                { id: 'hs_code', text: 'HS Code' },
                                { id: 'description', text: 'Description' }
                            ]}
                            value={searchBy}
                            onChange={setSearchBy}
                            placeholder="Search By"
                            valueKey="id"
                            displayKey="text"
                        />
                    </div>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="form-control"
                        style={{ flex: 2 }}
                    />
                    <div style={{ flex: 1 }}>
                        <Select2Dropdown
                            options={filterPermitTypes}
                            value={selectedPermitType}
                            onChange={handlePermitTypeChange}
                            placeholder="Filter by Permit Type"
                            valueKey="id"
                            displayKey="text"
                            clearable
                        />
                    </div>
                </div>
                <div className="table-container">
                    <table className="jobs-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('hs_code')} style={{ cursor: 'pointer' }}>HS Code {getSortIndicator('hs_code')}</th>
                                <th onClick={() => requestSort('description')} style={{ cursor: 'pointer' }}>Description {getSortIndicator('description')}</th>
                                <th onClick={() => requestSort('permit')} style={{ cursor: 'pointer' }}>Permit {getSortIndicator('permit')}</th>
                                {/* 11. Conditionally render Actions column header */}
                                {(hasPermission('PERMITS', 'can_edit') || hasPermission('PERMITS', 'can_delete')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="4">Loading...</td></tr>
                            ) : !debouncedSearchText && !selectedPermitType ? (
                                <tr><td colSpan="4">Permits Loaded</td></tr>
                            ) : debouncedSearchText.length < 3 && !selectedPermitType ? (
                                <tr><td colSpan="4">Enter at least 3 characters to search</td></tr>
                            ) : permits.length > 0 ? (
                                sortedPermits.map(p => (
                                    <tr key={p.id} style={{ backgroundColor: permitMappings[p.permit] || 'transparent' }}>
                                        <td>{p.hs_code}</td>
                                        <td>{p.description}</td>
                                        <td>{p.permit ? p.permit.toUpperCase() : ''}</td>
                                        {/* 12. Conditionally render the actions cell and buttons */}
                                        {(hasPermission('PERMITS', 'can_edit') || hasPermission('PERMITS', 'can_delete')) && (
                                            <td className="actions">
                                                {hasPermission('PERMITS', 'can_edit') && (
                                                    <button className="icon-btn edit-btn" onClick={() => handleOpenModal(p)} title="Edit">
                                                        <i className="fa fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                                {hasPermission('PERMITS', 'can_delete') && (
                                                    <button className="icon-btn delete-btn" onClick={() => handleDelete(p.id)} title="Delete">
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="4">No permits found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: '90vw', maxWidth: '600px' }}>
                        <div className="dialog-header">
                            <h3>{currentPermit.id ? 'Edit Permit' : 'Add New Permit'}</h3>
                            <button className="icon-btn" onClick={handleCloseModal} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body">
                            <div className="form-group">
                                <label>HS Code <span style={{color: 'red'}}>*</span></label>
                                <input type="text" name="hs_code" value={currentPermit.hs_code} onChange={handleFormChange} className="form-control" required />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <input type="text" name="description" value={currentPermit.description} onChange={handleFormChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Permit <span style={{color: 'red'}}>*</span></label>
                                <Select2Dropdown
                                    options={permitTypes}
                                    value={currentPermit.permit}
                                    onChange={(val) => setCurrentPermit(prev => ({ ...prev, permit: val }))}
                                    placeholder="Select or create a permit type"
                                    valueKey="id"
                                    displayKey="text"
                                    allowCreate={true}
                                    onCreateTag={handleCreatePermitType}
                                    required
                                />
                            </div>
                        </div>
                        <div className="dialog-footer">
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={handleCloseModal}>Cancel</button>
                            <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSave}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {showPermitConfigModal && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: '80vw', maxWidth: '900px' }}>
                        <div className="dialog-header">
                            <h3>Permit to Document Type Mapping</h3>
                            <button className="icon-btn" onClick={() => setShowPermitConfigModal(false)} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                            {/* 13. Pass hasPermission to the child component */}
                            <PermitConfiguration onClose={() => setShowPermitConfigModal(false)} hasPermission={hasPermission} />
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog {...confirmDialog} />
        </div>
    );
};

export default Permits;